import { useEffect, useState } from "react";
import {
  updateEventStatus,
  type EventInput,
} from "../../api/events";
import ErrorAlert from "../ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

const STATUS_OPTIONS: Array<{
  value: NonNullable<EventInput["status"]>;
  label: string;
}> = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

type Props = {
  eventId: number;
  status: string;
  onUpdated?: (status: string) => void;
  compact?: boolean;
};

export default function EventStatusControl({
  eventId,
  status,
  onUpdated,
  compact = false,
}: Props) {
  const [value, setValue] = useState(status);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(status);
  }, [status]);

  const isPublished = value === "published";

  async function handleChange(next: NonNullable<EventInput["status"]>) {
    if (next === value) return;
    setError(null);
    setIsSaving(true);
    const previous = value;
    setValue(next);
    try {
      const event = await updateEventStatus(eventId, next);
      onUpdated?.(event.status);
    } catch (err) {
      setValue(previous);
      setError(
        getUserFacingErrorMessage(err, "Could not update event status."),
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className={compact ? "" : "space-y-2"}>
      <div className={compact ? "flex flex-col gap-1" : "flex flex-wrap items-center gap-3"}>
        <select
          value={value}
          disabled={isSaving}
          onChange={(e) =>
            handleChange(e.target.value as NonNullable<EventInput["status"]>)
          }
          className={`border border-slate-300 bg-white text-slate-800 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 disabled:opacity-60 ${
            compact ? "px-2 py-1" : "px-3 py-2"
          }`}
          aria-label="Event visibility"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {!compact && (
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-sm ${
              isPublished
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-amber-50 text-amber-900 border border-amber-200"
            }`}
          >
            {isPublished ? "Visible to visitors" : "Hidden from visitors"}
          </span>
        )}
      </div>
      {error && (
        <ErrorAlert className={compact ? "mt-2" : ""} inline={compact}>
          {error}
        </ErrorAlert>
      )}
    </div>
  );
}
